import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Award, CheckCircle, Compass, Plane, Box, ShieldCheck, FileText, Home as HomeIcon, Glasses, Star, Sparkles } from 'lucide-react';
import { studioInfo, services } from '../mock';

const Home = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const isServicesInView = useInView(servicesRef, { once: true, margin: '-100px' });
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  const iconMap = {
    Compass,
    Plane,
    Box,
    ShieldCheck,
    FileText,
    Home: HomeIcon,
    Glasses
  };

  const stats = [
    { number: '500+', label: 'Progetti Completati', icon: CheckCircle },
    { number: '4400ha', label: 'Aerofotogrammetria', icon: Award },
    { number: '5+', label: 'Anni di Esperienza', icon: Star }
  ];

  return (
    <div className="bg-[#0A1628] overflow-hidden">
      {/* Hero Section with Enhanced Parallax */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Enhanced Parallax Background */}
        <motion.div
          style={{ y, scale }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1682663810771-89d21838530f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/70 via-[#0A1628]/85 to-[#0A1628]" />
        </motion.div>

        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(212,175,55,0.15) 2px, transparent 2px),
                             linear-gradient(90deg, rgba(212,175,55,0.15) 2px, transparent 2px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Floating Orbs */}
        <motion.div
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
          className="absolute top-20 left-10 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, 30, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2
          }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"
        />

        <motion.div
          style={{ opacity }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-24 text-center"
        >
          {/* Enhanced Title with Stagger Animation */}
          <div className="mb-8">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.6, 0.01, 0.05, 0.95] }}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 leading-tight"
            >
              <span className="block text-white font-extrabold tracking-tight">Topografia, progettazione,</span>
              <span className="block bg-gradient-to-r from-[#D4AF37] via-[#F4E5B8] to-[#D4AF37] bg-clip-text text-transparent font-extrabold tracking-tight">
                droni, BIM e realtà virtuale
              </span>
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
            className="text-xl md:text-2xl lg:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light"
          >
            per trasformare <span className="text-[#D4AF37] font-semibold">dati</span> e <span className="text-[#D4AF37] font-semibold">idee</span> in <span className="text-[#D4AF37] font-semibold">soluzioni concrete</span>.
          </motion.p>

          {/* Enhanced CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.6, 0.01, 0.05, 0.95] }}
            className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-20"
          >
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(212,175,55,0.4)' }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-10 py-5 bg-gradient-to-r from-[#D4AF37] via-[#F4E5B8] to-[#D4AF37] bg-size-200 bg-pos-0 hover:bg-pos-100 text-[#0A1628] rounded-full font-bold text-lg shadow-2xl transition-all duration-500 flex items-center space-x-3 overflow-hidden"
                style={{
                  backgroundSize: '200% auto'
                }}
              >
                <span className="relative z-10">Richiedi Preventivo</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300 relative z-10" />
              </motion.button>
            </Link>
            <Link to="/portfolio">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-full font-semibold text-lg hover:bg-white/10 hover:border-[#D4AF37]/50 transition-all duration-300 shadow-xl"
              >
                Vedi Portfolio
              </motion.button>
            </Link>
          </motion.div>

          {/* Enhanced Stats with Glassmorphism */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.6, 0.01, 0.05, 0.95] }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  whileHover={{ y: -8, scale: 1.05 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                  <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 text-center group-hover:border-[#D4AF37]/40 transition-all duration-500 shadow-xl">
                    <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-[#D4AF37] mx-auto mb-2 sm:mb-3" />
                    <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#D4AF37] mb-1 sm:mb-2 leading-none">{stat.number}</p>
                    <p className="text-gray-400 text-xs sm:text-sm font-medium">{stat.label}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Enhanced Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            className="w-7 h-12 border-2 border-[#D4AF37]/50 rounded-full flex justify-center pt-2"
          >
            <motion.div
              animate={{ y: [0, 16, 0], opacity: [1, 0, 1] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
              className="w-2 h-2 bg-[#D4AF37] rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Services Preview Section */}
      <section ref={servicesRef} className="relative py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isServicesInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block px-5 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-bold mb-6 tracking-wide"
            >
              I NOSTRI SERVIZI
            </motion.span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Soluzioni <span className="text-[#D4AF37]">Complete</span> per Ogni Esigenza
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Offriamo servizi tecnici avanzati con strumentazione all'avanguardia e personale altamente qualificato.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="group relative"
                >
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Card */}
                  <div className="relative h-full bg-gradient-to-br from-[#1A2B47]/80 to-[#0A1628]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 group-hover:border-[#D4AF37]/40 transition-all duration-500 shadow-2xl">
                    {/* Number Badge */}
                    <div className="absolute top-6 right-6 w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-xl flex items-center justify-center font-bold text-xl text-[#0A1628] shadow-lg transform group-hover:rotate-12 transition-transform duration-500">
                      {service.number}
                    </div>
                    
                    {/* Icon */}
                    <div className="w-16 h-16 bg-[#D4AF37]/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[#D4AF37]/20 transition-all duration-500">
                      <IconComponent className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#D4AF37] transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    {/* Link */}
                    <Link
                      to="/services"
                      className="inline-flex items-center space-x-2 text-[#D4AF37] font-semibold group-hover:gap-3 transition-all duration-300"
                    >
                      <span>Scopri di più</span>
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* View All Link */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-16"
          >
            <Link to="/services">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-3 px-8 py-4 bg-white/5 backdrop-blur-xl border-2 border-[#D4AF37]/40 text-[#D4AF37] rounded-full font-semibold text-lg hover:bg-[#D4AF37] hover:text-[#0A1628] transition-all duration-300 shadow-xl"
              >
                <span>Vedi Tutti i Servizi</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;